import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function InvoiceForm() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    member_id: '',
    recipient_name: '',
    recipient_email: '',
    recipient_address: '',
    date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    description: '',
    income_account_id: '',
    lines: [{ description: '', quantity: 1, unit_price: '', amount: '' }]
  });

  useEffect(() => {
    Promise.all([
      apiFetch('/api/members'),
      apiFetch('/api/accounts'),
      apiFetch('/api/settings')
    ]).then(([m, a, s]) => {
      setMembers(m);
      setAccounts(a);
      // Default income account
      const kontingent = a.find(acc => acc.account_number === '1010');
      if (kontingent) {
        setForm(prev => ({ ...prev, income_account_id: kontingent.id.toString() }));
      }
      // Default payment days from settings
      const days = s.default_payment_days || 30;
      setForm(prev => ({ ...prev, due_date: new Date(Date.now() + days * 86400000).toISOString().split('T')[0] }));
    }).catch(err => setError(err.message));
  }, [apiFetch]);

  const selectMember = (memberId) => {
    const member = members.find(m => m.id === parseInt(memberId));
    if (member) {
      setForm(prev => ({
        ...prev,
        member_id: memberId,
        recipient_name: [member.name, member.last_name].filter(Boolean).join(' '),
        recipient_email: member.email || '',
        recipient_address: [member.address, member.postal_code, member.city].filter(Boolean).join(', ')
      }));
    } else {
      setForm(prev => ({ ...prev, member_id: '' }));
    }
  };

  const updateLine = (index, field, value) => {
    const lines = [...form.lines];
    lines[index] = { ...lines[index], [field]: value };
    // Auto-calc amount
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(lines[index].quantity) || 0;
      const price = parseFloat(lines[index].unit_price) || 0;
      lines[index].amount = (qty * price).toFixed(2);
    }
    setForm(prev => ({ ...prev, lines }));
  };

  const addLine = () => {
    setForm(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit_price: '', amount: '' }]
    }));
  };

  const removeLine = (index) => {
    if (form.lines.length <= 1) return;
    setForm(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  const totalAmount = form.lines.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validLines = form.lines.filter(l => l.description && parseFloat(l.amount) > 0);
    if (validLines.length === 0) {
      setError('Tilføj mindst én fakturalinje med beskrivelse og beløb.');
      return;
    }

    if (!form.recipient_name) {
      setError('Angiv modtagerens navn.');
      return;
    }

    setSaving(true);
    try {
      const result = await apiFetch('/api/invoices', {
        method: 'POST',
        body: {
          ...form,
          income_account_id: form.income_account_id || null,
          lines: validLines
        }
      });
      navigate(`/app/fakturaer/${result.id}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const incomeAccounts = accounts.filter(a => a.type === 'income');

  return (
    <div>
      <div className="page-header">
        <h1>Ny faktura</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{marginBottom: '1.5rem'}}>
          <h3 style={{marginBottom: '1rem'}}>Modtager</h3>

          <div className="form-group">
            <label>Vælg medlem (valgfrit)</label>
            <select className="form-control" value={form.member_id}
              onChange={e => selectMember(e.target.value)}>
              <option value="">— Vælg medlem eller indtast manuelt —</option>
              {members.filter(m => m.is_active).map(m => (
                <option key={m.id} value={m.id}>{[m.name, m.last_name].filter(Boolean).join(' ')}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Navn *</label>
              <input className="form-control" value={form.recipient_name}
                onChange={e => setForm({...form, recipient_name: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" className="form-control" value={form.recipient_email}
                onChange={e => setForm({...form, recipient_email: e.target.value})}
                placeholder="Til afsendelse af faktura" />
            </div>
          </div>

          <div className="form-group">
            <label>Adresse</label>
            <input className="form-control" value={form.recipient_address}
              onChange={e => setForm({...form, recipient_address: e.target.value})} />
          </div>
        </div>

        <div className="card" style={{marginBottom: '1.5rem'}}>
          <h3 style={{marginBottom: '1rem'}}>Fakturadetaljer</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Fakturadato</label>
              <input type="date" className="form-control" value={form.date}
                onChange={e => setForm({...form, date: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Forfaldsdato</label>
              <input type="date" className="form-control" value={form.due_date}
                onChange={e => setForm({...form, due_date: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Indtægtskonto</label>
              <select className="form-control" value={form.income_account_id}
                onChange={e => setForm({...form, income_account_id: e.target.value})}>
                <option value="">— Vælg konto —</option>
                {incomeAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.account_number} - {a.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Beskrivelse (vises på faktura)</label>
            <input className="form-control" value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="F.eks. Kontingent 2026" />
          </div>
        </div>

        <div className="card" style={{marginBottom: '1.5rem'}}>
          <h3 style={{marginBottom: '1rem'}}>Fakturalinjer</h3>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{width: '40%'}}>Beskrivelse</th>
                  <th style={{width: '15%', textAlign: 'right'}}>Antal</th>
                  <th style={{width: '20%', textAlign: 'right'}}>Stykpris</th>
                  <th style={{width: '20%', textAlign: 'right'}}>Beløb</th>
                  <th style={{width: '5%'}}></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, i) => (
                  <tr key={i}>
                    <td>
                      <input className="form-control" value={line.description}
                        onChange={e => updateLine(i, 'description', e.target.value)}
                        placeholder="Beskrivelse" required />
                    </td>
                    <td>
                      <input type="number" className="form-control" value={line.quantity}
                        onChange={e => updateLine(i, 'quantity', e.target.value)}
                        style={{textAlign: 'right'}} min="0.01" step="0.01" />
                    </td>
                    <td>
                      <input type="number" className="form-control" value={line.unit_price}
                        onChange={e => updateLine(i, 'unit_price', e.target.value)}
                        style={{textAlign: 'right'}} placeholder="0,00" step="0.01" />
                    </td>
                    <td>
                      <input type="number" className="form-control" value={line.amount}
                        onChange={e => updateLine(i, 'amount', e.target.value)}
                        style={{textAlign: 'right', fontWeight: 600}} step="0.01" />
                    </td>
                    <td>
                      {form.lines.length > 1 && (
                        <button type="button" className="btn btn-danger btn-sm"
                          onClick={() => removeLine(i)}>X</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="button" className="btn btn-secondary" style={{marginTop: '0.75rem'}}
            onClick={addLine}>+ Tilføj linje</button>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', marginTop: '1rem',
            paddingTop: '1rem', borderTop: '2px solid #e2e8f0',
            fontSize: '1.2rem', fontWeight: 700
          }}>
            I alt: {totalAmount.toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.
          </div>
        </div>

        <div style={{display: 'flex', gap: '0.5rem'}}>
          <button type="submit" className="btn btn-primary" disabled={saving}
            style={{flex: 1, justifyContent: 'center', padding: '0.85rem'}}>
            {saving ? 'Opretter...' : 'Opret faktura'}
          </button>
          <button type="button" className="btn btn-secondary"
            onClick={() => navigate('/app/fakturaer')}
            style={{padding: '0.85rem'}}>
            Annuller
          </button>
        </div>
      </form>
    </div>
  );
}
